import ContentState from "../../Component/UI/ContentState";
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { errorMessage, httpStatus, platformApi } from './client';

export type AuthFailure = (status: number) => void;
export function useResource<T>(path: string, onAuth: AuthFailure) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const sequence = useRef(0);
  const reload = useCallback(() => setRevision(value => value + 1), []);
  useEffect(() => {
    const current = ++sequence.current;
    const controller = new AbortController();
    setLoading(true); setError(''); setData(null);
    platformApi.get<T>(path, {signal: controller.signal}).then(result => {
      if (current === sequence.current && !controller.signal.aborted) setData(result.data);
    }).catch(error => {
      if (current !== sequence.current || controller.signal.aborted) return;
      const status = httpStatus(error);
      if (status === 401 || status === 403) onAuth(status);
      setError(errorMessage(error));
    }).finally(() => {
      if (current === sequence.current && !controller.signal.aborted) setLoading(false);
    });
    return () => {controller.abort(); sequence.current += 1;};
  }, [path, revision, onAuth]);
  return {data, loading, error, reload};
}

export const number = (value: number | null | undefined) => typeof value === 'number' && Number.isFinite(value) ? new Intl.NumberFormat('ko-KR').format(value) : '—';
export const money = (value: number | null | undefined) => typeof value === 'number' && Number.isFinite(value) ? `${number(value)}원` : '—';
export function dateTime(value: string | null | undefined, dateOnly = false) {
  if (!value) return '—';
  // Legacy MySQL DATETIME strings are Korean wall time, independent of the viewer's device zone.
  const normalized = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(value) ? `${value.replace(' ', 'T')}+09:00` : value;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('ko-KR', {timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit', ...(dateOnly ? {} : {hour: '2-digit', minute: '2-digit'})}).format(date);
}
export function currentMonth() {
  const today = new Intl.DateTimeFormat('en-CA', {timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit'}).format(new Date());
  return {from: `${today.slice(0, 7)}-01`, to: today};
}
export function ResourceState({loading, error, retry, children}: {loading: boolean; error: string; retry: () => void; children: ReactNode}) {
  if (loading) return <ContentState kind="loading" icon="document" title="데이터를 불러오는 중입니다." />;
  if (error) return <ContentState kind="error" icon="document" title="데이터를 불러오지 못했습니다." description={error} action={{ label: "다시 시도", onClick: retry }} />;
  return <>{children}</>;
}
export function Empty({children = '조회된 데이터가 없습니다.'}: {children?: ReactNode}) {
  return <ContentState compact icon="document" title={children} />;
}
export function Badge({value, children}: {value?: string; children: ReactNode}) {
  return <span className={`pa-badge pa-badge-${value || 'neutral'}`}>{children}</span>;
}
export function Stat({label, value, note, accent = false}: {label: string; value: string; note?: string; accent?: boolean}) {
  return <article className={`pa-stat${accent ? ' pa-stat-accent' : ''}`}><span>{label}</span><strong>{value}</strong>{note && <small>{note}</small>}</article>;
}
export function Pagination({page, total, limit, setPage}: {page: number; total: number; limit: number; setPage: (page: number) => void}) {
  const pages = Math.max(1, Math.ceil(total / limit));
  useEffect(() => {if (page > pages) setPage(pages);}, [page, pages, setPage]);
  return <div className="pa-pagination"><span>총 {number(total)}건</span><div><button type="button" className="pa-button pa-button-small" disabled={page <= 1} onClick={() => setPage(page - 1)} aria-label="이전 페이지">← 이전</button><span>{page} / {pages}</span><button type="button" className="pa-button pa-button-small" disabled={page >= pages} onClick={() => setPage(page + 1)} aria-label="다음 페이지">다음 →</button></div></div>;
}
export function SectionHeading({title, description, children}: {title: string; description?: string; children?: ReactNode}) {
  return <div className="pa-section-heading"><div><h2>{title}</h2>{description && <p>{description}</p>}</div>{children}</div>;
}
export function Icon({name}: {name: string}) {
  const paths: Record<string, ReactNode> = {
    analytics: <><path d="M4 3v17h17M8 15V9m5 6V5m5 10v-4"/></>,
    'pg-settings': <><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 10h18M7 15h4M16 14v2M15 15h2"/></>,
    overview: <><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></>,
    users: <><circle cx="9" cy="8" r="3"/><path d="M3 21v-2a6 6 0 0 1 12 0v2M16 4a3 3 0 0 1 0 6M18 14a5 5 0 0 1 3 5v2"/></>,
    sales: <><path d="M4 3v18h17M8 16v-5M13 16V6M18 16V9"/></>,
    settlements: <><path d="M4 7h16v13H4zM7 4h10v3M8 12h8M8 16h5M17 12h.01"/></>,
    deposits: <><path d="M3 9h18M3 21h18M5 9v12M10 9v12M14 9v12M19 9v12M2 8l10-6 10 6M9 16h6"/></>,
    sellers: <><path d="m3 9 2-6h14l2 6M3 9v3a3 3 0 0 0 5 2 3 3 0 0 0 4 0 3 3 0 0 0 4 0 3 3 0 0 0 5-2V9M5 15v6h14v-6M10 21v-4h4v4"/></>,
    policies: <><path d="M14 3H5v18h14V8zM14 3v5h5M8 12h8M8 16h6"/></>,
    operations: <><path d="M12 3 3 7v5c0 5 9 9 9 9s9-4 9-9V7zM8 12l3 3 5-6"/></>,
    audit: <><path d="M7 3h10v4H7zM6 5H4v16h16V5h-2M8 11h8M8 15h8"/></>,
  };
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name]}</svg>;
}
